import { API } from 'aws-amplify';
import React from 'react';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { AgentSelect } from '../types';
import {
  ImpersonationSearch,
  SearchAgent,
} from './alliance/impersonation-search';

/** An AgentSelect from /agent/search as a SearchAgent row */
const toSearchAgent = (params: { agent: AgentSelect }): SearchAgent => {
  // Params
  const { agent } = params;
  const name =
    [agent.Fname, agent.Lname].filter(Boolean).join(' ') ||
    agent.AgtName ||
    agent.AgtNo ||
    '';
  return {
    id: agent.AgtNo || '',
    name,
    city: agent.HomeCity,
    state: agent.HomeSt,
    status: agent.AgtStatus,
    raw: agent,
  };
};

/**
 * The sidebar's impersonation search, wired to Contracting: searches through
 * the Contracting API's /agent/search (which scopes results to what the user
 * may see) and impersonates by storing the AgentSelect, as before.
 */
export function SelectAgentImpersonate() {
  // Context
  const { state } = React.useContext(AuthContext);
  // Hooks
  const identity = useAllianceIdentity();
  const { startImpersonating, stopImpersonating } = useSessionActions();

  const handleSearch = React.useCallback(async (params: { query: string }) => {
    const response: { data: AgentSelect[] } = await API.post(
      'ContractingAPI',
      '/agent/search',
      { body: { search: params.query } },
    );
    return (response.data || [])
      .filter((agent) => agent.AgtNo)
      .map((agent) => toSearchAgent({ agent }));
  }, []);

  if (!identity) {
    return null;
  }

  const impersonated = state.impersonatedAgent;
  const active = impersonated
    ? {
        id: impersonated.AgtNo || '',
        name: toSearchAgent({ agent: impersonated }).name,
      }
    : null;

  return (
    <ImpersonationSearch
      placement="sidebar"
      scope={identity.role}
      staffAgentName={identity.staffFor?.fullName}
      owner={state.user?.getUsername() || ''}
      active={active}
      onSearch={handleSearch}
      onPick={(agent) => startImpersonating(agent.raw as AgentSelect)}
      onStop={stopImpersonating}
      excludeIds={[identity.user.id, identity.staffFor?.id || ''].filter(
        Boolean,
      )}
    />
  );
}
